import React, {
  useState,
  ForwardRefRenderFunction,
  useCallback,
  useImperativeHandle,
  forwardRef,
  useRef,
  ReactFragment,
  useEffect,
} from "react";
import { useTranslation } from "react-i18next";
import { Grid, Modal, Tooltip } from "@mui/material";
import { IoMdClose } from "react-icons/io";
import useStyles, { skeletonStyles } from "./styles";
import ShipeeziGrid from "Components/AppGrid/shipeezi-new-grid";
import { MdOutlineCancel, MdOutlineChangeCircle } from "react-icons/md";
import { FiEdit } from "react-icons/fi";
import _ from "lodash";

import { PriceGroup } from "../../../../../../models/price.group";
import { useAppState } from "../../../../../../context/StateContext";
import { useMutation, useQuery } from "@tanstack/react-query";
import PriceGroupService from "../../../../../../services/price.group.service";
import { NotificationToast } from "../../../../../../components/Generic/Toast/NotificationToast";
import { Badge } from "reactstrap";
import { Status } from "../../../../../../util/enums";
import classnames from "classnames";
import SkeletonComponent from "../../../../../../components/Generic/Skeleton";
import { Input } from "../../../../../../components/Form";
import { FaSave } from "react-icons/fa";

enum KeyboardKey {
  ENTER = "Enter",
  ESCAPE = "Escape",
}

enum IconColor {
  DISABLED = "#D0D5DD",
  DANGER = "#D92D20",
  SAVE = "#308CDB",
  NORMAL = "",
}
const ICON_SIZE = 20;

interface ServiceSection {
  [groupId: number]: HTMLDivElement | null;
}

export interface PriceGroupPageHandles {
  openScreen: () => void;
  closeScreen: () => void;
}

interface PriceGroupPageProps {}
const PriceGroupPage: ForwardRefRenderFunction<PriceGroupPageHandles, PriceGroupPageProps> = (
  props,
  ref
) => {
  const { t } = useTranslation();
  const { currentUser } = useAppState();
  const classes = useStyles();

  const [modalOpened, setModalOpened] = useState(false);
  const [allGroups, setAllGroups] = useState<PriceGroup[]>([]);

  const [editingGroupId, setEditingGroupid] = useState(0);
  const [newGroupName, setNewGroupName] = useState("");
  const [updatingGroup, setUpdatingGroup] = useState(0);

  const inputRef = useRef<HTMLInputElement>(null);

  const [serviceSectionRefs, setServiceSectionRef] = useState<ServiceSection>({} as ServiceSection);

  const openScreen = useCallback(() => {
    refetchGroups();
    setModalOpened(true);
  }, []);
  const closeScreen = useCallback(() => {
    setAllGroups([]);
    setModalOpened(false);
  }, []);

  useImperativeHandle(ref, () => ({
    openScreen,
    closeScreen,
  }));

  const { isFetching: loadingGroups, refetch: refetchGroups } = useQuery(
    ["groups.list"],
    () =>
      PriceGroupService.getAll({
        companyId: currentUser?.companyId,
        include: JSON.stringify([{ association: "services" }]),
        sortBy: JSON.stringify([["id", "desc"]]),
      }),
    {
      refetchOnWindowFocus: false,
      enabled: !!currentUser?.companyId && !!modalOpened,
      onSuccess: (response) => {
        if (response && response.data && response.data instanceof Array) {
          setAllGroups(response.data);
        }
      },
      onError: (error: Error) => {
        if (error && error.message) {
          NotificationToast.error(error?.message);
        }
      },
    }
  );

  const { refetch: refetchGroup } = useQuery(
    ["groups.get"],
    () =>
      PriceGroupService.getById(updatingGroup, {
        include: JSON.stringify([{ association: "services" }]),
      }),
    {
      refetchOnWindowFocus: false,
      enabled: false,
      onSuccess: (response) => {
        if (response && response.data && response.data.id) {
          setAllGroups((prev) => {
            return prev.map((group) => {
              if (group && group.id && String(group.id) === String(response.data.id))
                return response.data;
              else return group;
            });
          });
          setUpdatingGroup(0);
        }
      },
    }
  );

  const { mutate: updateGroup } = useMutation(
    (group: PriceGroup) => PriceGroupService.update(Number(group.id), group),
    {
      onMutate: (group) => {
        setUpdatingGroup(Number(group.id));
      },
      onSuccess: (_, group) => {
        NotificationToast.success(t("groupUpdatedSuccessfully", { groupName: group.name }), { duration: 1000 });
        onCancelEditingGroupName();
        refetchGroup();
      },
    }
  );

  const GroupStatusBadge = (status: string) => {
    const label = `${status} group`;
    return (
      <Badge
        className={classnames(
          "badgeComponent",
          classes.statusBadge,
          status === Status.ACTIVE ? classes.groupBadgeActive : classes.groupBadgeInactive
        )}
      >
        {label}
      </Badge>
    );
  };

  const badgeStatus = (status: string) => (
    <Badge
      className={classnames("badgeComponent", classes.serviceBadge, classes.statusBadge, {
        [classes.serviceBadgeActive]: status === Status.ACTIVE,
        [classes.serviceBadgeInactive]: status !== Status.ACTIVE,
      })}
    >
      {status}
    </Badge>
  );

  const invertStatus = useCallback((status?: string) => {
    if (!status) return status;
    return status === Status.ACTIVE ? Status.INACTIVE : Status.ACTIVE;
  }, []);

  const onChangeStatus = useCallback(
    (priceGroup: PriceGroup) => {
      updateGroup({
        ...priceGroup,
        status: invertStatus(priceGroup.status),
      });
    },
    [updateGroup]
  );

  const onOpenEditingGroupName = useCallback((group?: PriceGroup) => {
    if (!group || !group.id) return;
    setEditingGroupid(group.id);
    setNewGroupName(group?.name || "");
    setTimeout(() => {
      inputRef.current?.focus();
    }, 100);
  }, []);

  const onCancelEditingGroupName = useCallback(() => {
    setNewGroupName("");
    setEditingGroupid(0);
  }, []);

  const onSaveGroupName = useCallback(
    (group?: PriceGroup) => {
      if (!group) return;
      updateGroup({
        ...group,
        name: newGroupName,
      });
    },
    [newGroupName]
  );

  const createSectionRef = useCallback((groupId?: number, ref?: any) => {
    if (!groupId || !ref) return;
    setServiceSectionRef((prev) => {
      prev[groupId] = ref;
      return prev;
    });
  }, []);

  return (
    <Modal open={modalOpened} onClose={closeScreen} data-cy="modal-group-screen">
      <div className={classes.container} data-cy="group-container">
        <Grid
          className={classes.header}
          data-cy="group-header"
          container
          justifyContent="space-between"
          alignItems="center"
        >
          <Grid item>
            <span className={classes.title} data-cy="group-screen-title">
              {t("groups")}
            </span>
          </Grid>

          <Grid item className={classes.closeContainer}>
            <Tooltip title={t("closeThisScreen")} placement="top">
              <div onClick={closeScreen} data-cy="group-screen-close">
                <IoMdClose size={16} className={classes.closeIcon} />
              </div>
            </Tooltip>
          </Grid>
        </Grid>

        <Grid container className={classes.content} data-cy="group-content">
          <SkeletonComponent isLoading={loadingGroups} classesName={classes.groupListSkeleton}>
            <>
              {(!allGroups || allGroups.length <= 0) && <EmptyGroup />}
              {allGroups.map((group) => (
                <Grid
                  key={group.id}
                  container
                  data-cy={`group-${group.name}`}
                  className={classes.groupSection}
                  ref={(ref: any) => createSectionRef(group.id, ref)}
                >
                  <ServiceSkeleton
                    loading={updatingGroup === group.id}
                    height={serviceSectionRefs[Number(group.id)]?.offsetHeight || 52}
                  >
                    <>
                      <Grid
                        className={classes.groupInformation}
                        container
                        direction="row"
                        alignItems="center"
                        justifyContent="space-between"
                      >
                        {editingGroupId === group.id ? (
                          <Grid
                            className={classes.groupNameEdit}
                            item
                            xs={6}
                            md={6}
                            lg={6}
                            container
                            direction="row"
                            alignItems="center"
                            wrap="nowrap"
                          >
                            <Input
                              value={newGroupName}
                              name={`group-name-input-${group.name}`}
                              onChange={(e) => setNewGroupName(e.target.value)}
                              placeholder={t("fillNameAndPressSaveOrEnter")}
                              inputRef={inputRef}
                              className={classes.groupNameInput}
                              onKeyDown={(e) => {
                                if (e.key === KeyboardKey.ENTER) {
                                  onSaveGroupName(group);
                                }
                                if (e.key === KeyboardKey.ESCAPE) {
                                  onCancelEditingGroupName();
                                }
                              }}
                            />
                            <div className={classes.groupSaveOrCancel}>
                              <Tooltip title={t("pressToConfirmGroupNameChange")} placement="top">
                                <div
                                  data-cy={`group-save-${group.name}`}
                                  onClick={() => onSaveGroupName(group)}
                                >
                                  <FaSave
                                    size={ICON_SIZE}
                                    color={IconColor.SAVE}
                                    className={classnames(classes.icon, "ml-3")}
                                  />
                                </div>
                              </Tooltip>
                              <Tooltip title={t("pressToCancelGroupNameChange")} placement="top">
                                <div
                                  data-cy={`group-cancel-${group.name}`}
                                  onClick={() => onCancelEditingGroupName()}
                                >
                                  <MdOutlineCancel
                                    size={ICON_SIZE}
                                    color={IconColor.DANGER}
                                    className={classnames(classes.icon)}
                                  />
                                </div>
                              </Tooltip>
                            </div>
                          </Grid>
                        ) : (
                          <Grid item data-cy={`group-name-${group.name}`}>
                            <span className={classes.groupName}>{group.name}</span>
                          </Grid>
                        )}

                        <Grid item className={classes.groupActions}>
                          {!(editingGroupId === group.id) && (
                            <Tooltip title={t("editGroupName")} placement="top">
                              <div
                                data-cy={`group-edit-${group.name}`}
                                onClick={() => onOpenEditingGroupName(group)}
                              >
                                {/* <FiEdit size={ICON_SIZE} className={classes.icon} /> */}
                              </div>
                            </Tooltip>
                          )}

                          <Tooltip title={t("changeGroupStatus")} placement="top">
                            <div
                              data-cy={`group-change-status-${group.name}`}
                              onClick={() => onChangeStatus(group)}
                            >
                              <MdOutlineChangeCircle size={ICON_SIZE} className={classes.icon} />
                            </div>
                          </Tooltip>

                          <span data-cy={`group-status-${group.name}`}>
                            {group.status && GroupStatusBadge(group.status)}
                          </span>
                        </Grid>
                      </Grid>

                      <Grid className={classes.serviceSection} container direction="column">
                        <Grid item className={classes.serviceLabelContainer}>
                          <span className={classes.serviceLabel}>{t("services")}</span>
                        </Grid>
                        <Grid item className={classes.serviceTable}>
                          {group.services && group.services.length > 0 && (
                            <ShipeeziGrid
                              showFooterGrid={false}
                              showFooterPageInfo={false}
                              showHeaderGrid={false}
                              showSelection={false}
                              showSort={false}
                              data={group.services}
                              columns={[
                                { name: "code", title: t("code") },
                                {
                                  name: "sellType",
                                  title: t("type"),
                                },
                                {
                                  name: "sellDescription",
                                  title: t("description"),
                                },
                                {
                                  name: "status",
                                  title: t("status"),
                                  formatComponent: (status: string) =>
                                    status && badgeStatus(status),
                                },
                              ]}
                            />
                          )}
                          {(!group.services || group.services.length <= 0) && (
                            <span
                              className={classes.serviceEmpty}
                              data-cy={`group-${group.name}-services-empty`}
                            >
                              {t("noAssociatedService")}
                            </span>
                          )}
                        </Grid>
                      </Grid>
                    </>
                  </ServiceSkeleton>
                </Grid>
              ))}
            </>
          </SkeletonComponent>
        </Grid>
      </div>
    </Modal>
  );
};

export default forwardRef(PriceGroupPage);

interface ServiceSkeletonProps {
  height: string | number;
  loading: boolean;
}

export const ServiceSkeleton: React.FC<ServiceSkeletonProps> = ({ height, loading, children }) => {
  const classes = skeletonStyles({ height })();
  return (
    <SkeletonComponent isLoading={loading} classesName={classes.skeleton}>
      <>{children}</>
    </SkeletonComponent>
  );
};

export const EmptyGroup: React.FC = () => {
  const classes = useStyles();
  const { t } = useTranslation();

  return (
    <div className={classes.emptyGroup} data-cy="group-list-empty">
      <div className={classes.emptyGroupImage}>
        <img
          data-cy="group.list-empty-img"
          src={require("../../../../../../assets/img/groups-services.svg")}
          width="100%"
          height="100%"
        />
      </div>
      <span data-cy="group.list-empty-label" className={classes.empttyGroupMessage}>
        {t("seemsLikeYouDidnTCreateAnyGroup")}
      </span>
    </div>
  );
};
